import { httpClient } from './http-client';

export type RiskLevel = 'LOW' | 'MODERATE' | 'HIGH' | 'EMERGENCY';

export interface AISuggestion {
  id: string;
  conversationId: string;
  type: string;
  content: Record<string, unknown>;
  confidence?: number;
  accepted?: boolean | null;
  createdAt?: string;
}

export interface AISummaryResponse {
  conversationId: string;
  summary: Record<string, unknown>;
  suggestions: Array<{ type: string; content: Record<string, unknown>; confidence?: number }>;
  tokenUsage: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    cost: number;
  };
}

export interface AIAuditEntry {
  id?: string;
  conversationId: string;
  action: string;
  newData?: Record<string, unknown>;
  oldData?: Record<string, unknown>;
  createdAt?: string;
}

export interface TriageRequest {
  patientId: string;
  appointmentId?: string;
  symptoms: Array<{ name: string; intensity?: number; duration?: string }>;
  chiefComplaint: string;
  historyOfPresentIllness?: string;
  currentMedications?: string[];
  allergies?: string[];
}

export interface TriageResult {
  conversationId: string;
  triageId: string;
  riskClassification: RiskLevel;
  chiefComplaint: string;
  symptoms: Array<{ name: string; intensity?: number; duration?: string }>;
  duration?: string;
  severity?: string;
  redFlags: string[];
  recommendedSpecialty?: string;
  summary: string;
  requiresImmediateCare: boolean;
}

export interface CopilotSuggestionSet {
  cidCodes: AISuggestion[];
  exams: AISuggestion[];
  conduct: AISuggestion[];
  prescriptions: AISuggestion[];
  allSuggestions: AISuggestion[];
}

export interface SOAPNote {
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
  metadata: {
    chiefComplaint: string;
    symptoms: string[];
    duration?: string;
    physicalExam?: string;
    diagnoses: Array<{ description: string; cidCode?: string }>;
    prescriptions: string[];
    exams: string[];
    recommendations: string[];
    followUp?: string;
  };
}

export interface ScribeResult {
  conversationId: string;
  soap: SOAPNote;
}

export interface TimelineEvent {
  date: string;
  type: string;
  title: string;
  description: string;
  metadata?: Record<string, unknown>;
}

export interface TimelineSummary {
  conversationId: string;
  patientId: string;
  events: TimelineEvent[];
  patterns: string[];
  progressionNotes: string[];
  totalEvents: number;
  generatedAt: string;
}

export interface AIAlert {
  id: string;
  conversationId?: string;
  patientId: string;
  medicalRecordId?: string;
  type: string;
  severity: 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL';
  title: string;
  description: string;
  evidence?: Record<string, unknown>;
  status: 'PENDING' | 'ACKNOWLEDGED' | 'RESOLVED' | 'DISMISSED';
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt?: string;
}

export interface ProviderMetricSummary {
  totalCalls: number;
  successCount: number;
  failureCount: number;
  avgLatencyMs: number;
  totalCostUsd: number;
  totalTokens: number;
  fallbackCount: number;
}

export const aiApi = {
  async generateSummary(payload: { medicalRecordId: string; patientId: string; doctorId?: string; appointmentId?: string }): Promise<AISummaryResponse> {
    return await httpClient.post<AISummaryResponse>('/ai/summary/generate', payload);
  },

  async suggestCID(payload: { diagnosis: string; symptoms?: string[] }): Promise<AISuggestion[]> {
    return await httpClient.post<AISuggestion[]>('/ai/suggest/cid', payload);
  },

  async suggestExams(payload: { diagnosis: string; symptoms?: string[] }): Promise<AISuggestion[]> {
    return await httpClient.post<AISuggestion[]>('/ai/suggest/exams', payload);
  },

  async reviewSuggestion(payload: { suggestionId: string; accepted: boolean; reviewedBy: string; feedback?: string }): Promise<AISuggestion> {
    return await httpClient.post<AISuggestion>('/ai/suggest/review', payload);
  },

  async getAuditLog(conversationId: string): Promise<AIAuditEntry[]> {
    return await httpClient.get<AIAuditEntry[]>(`/ai/audit/${conversationId}`);
  },

  async performTriage(payload: TriageRequest): Promise<TriageResult> {
    return await httpClient.post<TriageResult>('/ai/triage', payload);
  },

  async copilotSuggest(payload: {
    conversationId: string;
    patientId: string;
    doctorId: string;
    context: { diagnosis?: string; symptoms?: Array<{ name: string }>; medicalRecordSummary?: Record<string, unknown> };
    types?: string[];
  }): Promise<CopilotSuggestionSet> {
    return await httpClient.post<CopilotSuggestionSet>('/ai/copilot', payload);
  },

  async generateSOAP(payload: {
    appointmentId: string;
    patientId: string;
    doctorId: string;
    transcript: Array<{ speaker: 'doctor' | 'patient' | 'system'; text: string; timestamp?: string }>;
  }): Promise<ScribeResult> {
    return await httpClient.post<ScribeResult>('/ai/scribe', payload);
  },

  async generateTimeline(payload: {
    patientId: string;
    medicalHistory?: {
      consultations?: Array<{ date: string; specialty?: string; summary?: string; doctorName?: string }>;
      prescriptions?: Array<{ date: string; medications: string[]; doctorName?: string }>;
      diagnoses?: Array<{ date: string; description: string; cidCode?: string }>;
      exams?: Array<{ date: string; exam: string; result?: string }>;
      certificates?: Array<{ date: string; reason: string; daysOff?: number }>;
      evolutions?: Array<{ date: string; doctorName?: string; summary: string }>;
      allergies?: string[];
      chronicDiseases?: string[];
    };
  }): Promise<TimelineSummary> {
    return await httpClient.post<TimelineSummary>('/ai/timeline', payload);
  },

  async checkAlerts(payload: {
    patientId: string;
    medicalRecordId?: string;
    conversationId?: string;
    proposedMedications: string[];
    patientAllergies?: string[];
    patientChronicDiseases?: string[];
    currentMedications?: string[];
    pregnancy?: boolean;
    age?: number;
  }): Promise<AIAlert[]> {
    return await httpClient.post<AIAlert[]>('/ai/alerts/check', payload);
  },

  async getMetricsSummary(provider?: string, sinceHours?: number): Promise<ProviderMetricSummary> {
    const queryParams: string[] = [];
    if (provider) queryParams.push(`provider=${encodeURIComponent(provider)}`);
    if (sinceHours) queryParams.push(`sinceHours=${sinceHours}`);
    const qs = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    return await httpClient.get<ProviderMetricSummary>(`/ai/metrics/summary${qs}`);
  },

  async getSessionMemory(conversationId: string) {
    return await httpClient.get<unknown[]>(`/ai/memory/${conversationId}`);
  },
};